import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { PolicyNote } from '../../design/primitives';

export interface PolicyBlock {
  policy: 'gated' | 'full' | 'facts' | string;
  bar?: string | null;
  min_tier?: string;
  hidden_count?: number;
  hidden_by_tier?: Record<string, number>;
  why_hidden?: number;
  next_hidden?: number;
  next_withheld?: string | null;
  raise_to?: string | null;
  raised?: boolean;
  /** connections below the bar, counted apart from the drivers (#426) */
  connections_hidden?: number;
  /** a persisted v2 view whose policy was re-applied on this read (#426) */
  reapplied?: boolean;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * What the viewer's evidence policy hid (#187), said plainly: "+2 lower-
 * evidence drivers below your bar — raise to Balanced" for a gated viewer,
 * or the facts-only note that WHAT NEXT is withheld. Raising is for this
 * answer only and is not remembered.
 */
export default function HiddenNote({ policy, count, what = 'drivers', onRaise, raising }: {
  policy: PolicyBlock | null | undefined;
  count?: number;
  what?: string;
  onRaise?: (bar: string) => void;
  raising?: boolean;
}) {
  if (!policy) return null;
  if (policy.policy === 'facts' && policy.next_withheld && what === 'suggestions') {
    return (
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }} data-testid="hidden-note" data-kind="withheld">
        {policy.next_withheld}
      </Typography>
    );
  }
  const n = count ?? policy.hidden_count;
  if (typeof n !== 'number' || n === 0 || policy.policy !== 'gated') return null;
  const note = <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      +{n} lower-evidence {n === 1 ? what.replace(/s$/, '') : what} below your bar{policy.bar ? ` (${cap(policy.bar)})` : ''}
    </Typography>;
  const action = policy.raise_to && onRaise ? <Button size="small" variant="text" disabled={!!raising} onClick={() => onRaise(policy.raise_to as string)} sx={{ py: 0, minHeight: 0, fontSize: '0.72rem' }}>
      {raising ? 'Raising…' : `Raise to ${cap(policy.raise_to)}`}
    </Button> : undefined;
  return (
    <PolicyNote action={action} data-testid="hidden-note" data-kind="below-bar">
    <Box>{note}</Box>
    </PolicyNote>
  );
}
